import React from 'react';
import LegalPageLayout, { Section, Paragraph, List, Highlight, Example } from '../../components/LegalPageLayout';

const ContentGuidelines = () => {
    return (
        <LegalPageLayout title="Review Content Guidelines" lastUpdated="March 2026">
            <Section title="Writing Effective Reviews">
                <Paragraph>
                    ClientCheck reviews are most valuable when they are factual, professional, and 
                    focused on the job experience. These guidelines will help you write reviews that 
                    are useful to other contractors while staying within platform policies.
                </Paragraph>
            </Section>

            <Section title="Review Requirements">
                <Paragraph>
                    All reviews must be:
                </Paragraph>
                <List items={[
                    "Factual — based on your actual experience at the job",
                    "Job-related — focused on work-related aspects of the experience",
                    "Professional — written with neutral, business-appropriate language",
                    "First-hand — from jobs you personally worked on"
                ]} />
            </Section>

            <Section title="Structured Ratings">
                <Paragraph>
                    ClientCheck uses structured ratings across six categories:
                </Paragraph>
                <List items={[
                    "Payment — Was payment made on time and as agreed?",
                    "Communication — Was communication clear and responsive?",
                    "Professionalism — Was the interaction conducted professionally?",
                    "Schedule — Were timelines and schedules respected?",
                    "Change Orders — Were scope changes handled fairly?",
                    "Site Access — Was jobsite access consistent and reliable?"
                ]} />
                <Paragraph>
                    These structured ratings are preferred over free-text notes because they provide 
                    consistent, comparable data that benefits the contractor community.
                </Paragraph>
            </Section>

            <Section title="Writing Notes">
                <Paragraph>
                    The optional notes field allows you to add brief context to your review. 
                    Notes are limited to 250 characters and must follow these guidelines:
                </Paragraph>
                <List items={[
                    "Stick to factual statements about what happened",
                    "Avoid naming individuals or including personal details",
                    "Use neutral, professional language",
                    "Focus on job-relevant information"
                ]} />
            </Section>

            <Section title="Examples">
                <Paragraph>
                    Here are examples of appropriate and inappropriate review content:
                </Paragraph>
                
                <h3 className="font-medium text-foreground mt-4 mb-2">Payment Issues</h3>
                <Example allowed={true} text="Payment was delayed 30 days past the invoice due date." />
                <Example allowed={false} text="They're crooks who never pay anyone!" />
                
                <h3 className="font-medium text-foreground mt-4 mb-2">Communication</h3>
                <Example allowed={true} text="Responses typically took 5-7 days during the project." />
                <Example allowed={false} text="The owner John Smith ignored all my calls." />
                
                <h3 className="font-medium text-foreground mt-4 mb-2">Schedule Changes</h3>
                <Example allowed={true} text="Project was paused for 3 weeks mid-construction." />
                <Example allowed={false} text="Total disaster. These people wasted months of my time." />
                
                <h3 className="font-medium text-foreground mt-4 mb-2">General Experience</h3>
                <Example allowed={true} text="Scope expanded twice without contract amendments." />
                <Example allowed={false} text="Worst job ever. Stay away from this address!!!" />
            </Section>

            <Section title="Context Modifiers">
                <Paragraph>
                    ClientCheck allows you to add context that may explain your ratings. 
                    Available context options include:
                </Paragraph>
                <List items={[
                    "Management company involved",
                    "Accounting/payment processing delay",
                    "Insurance claim involved",
                    "Permit or inspection delay",
                    "Weather delay",
                    "Scope changed mid-job",
                    "Client requested pause",
                    "Contractor-caused delay",
                    "Shared fault situation",
                    "Unresolved punch list items"
                ]} />
                <Paragraph>
                    These modifiers help provide fair context and are factored into the experience 
                    score calculation.
                </Paragraph>
            </Section>

            <Section title="Content That Will Be Blocked">
                <Paragraph>
                    Our automated content filter will block submissions containing:
                </Paragraph>
                <List items={[
                    "Profanity or vulgar language",
                    "Personal names",
                    "Criminal accusations",
                    "Threats or harassment",
                    "Discriminatory language",
                    "Contact information"
                ]} />
                <Paragraph>
                    If your submission is blocked, revise it to remove prohibited content and 
                    resubmit using factual, professional language.
                </Paragraph>
            </Section>

            <Section title="Review Accuracy">
                <Highlight>
                    You are responsible for the accuracy of your reviews. Submitting false 
                    information may result in account suspension or termination.
                </Highlight>
                <Paragraph>
                    Only submit reviews for jobs you actually performed. Reviews should accurately 
                    reflect your experience to the best of your knowledge.
                </Paragraph>
            </Section>
        </LegalPageLayout>
    );
};

export default ContentGuidelines;
